<template>
  <agent-default>
    <div v-if="!isAgentNotFound" class="dashboard-body">
      <!-- Referral Banner -->
      <div
        class="tw-mb-6 tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white"
        style="background: linear-gradient(to right, rgba(43, 174, 226, 0.08), rgba(43, 174, 226, 0));"
      >
        <div class="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between tw-gap-4">
          <div>
            <h4 class="c-navy-text tw-font-semibold tw-mb-1">Your Agent Referral Link</h4>
            <p class="tw-text-sm c-gray-text">Share this link with your clients to earn commissions on new signups.</p>
          </div>
          <div class="tw-flex tw-gap-2 tw-items-center tw-flex-wrap">
            <v-text-field
              :value="referralLink"
              readonly
              outlined
              dense
              hide-details
              class="tw-max-w-xs"
            />
            <v-btn color="primary" outlined @click="copyReferralLink">
              <i class="ph ph-copy tw-mr-1" style="font-size: 18px;"></i>
              {{ copySuccess ? 'Copied!' : 'Copy' }}
            </v-btn>
            <v-btn color="primary" class="tw-ml-2" :to="{ name: 'AgentProductSelect' }" @click="trackNewQuoteClick">
              <v-icon small class="tw-mr-1">mdi-plus</v-icon>
              New Quote
            </v-btn>
          </div>
        </div>
      </div>

      <h2 class="c-navy-text tw-text-xl tw-font-bold tw-mb-6">Financial Overview</h2>

       <!-- Snapshot Grid -->
        <div class="snapshot-grid">

          <!-- Paid Commissions -->
          <div class="stat-card clickable" @click="openCommissions('Y')">
            <template v-if="loading">

              <div class="shimmer-icon"></div>

              <div class="stat-details shimmer-details">
                <div class="shimmer shimmer-label"></div>
                <div class="shimmer shimmer-value"></div>
              </div>

            </template>

            <template v-else>

              <div class="stat-icon">
                <i class="ph ph-chart-line-up"></i>
              </div>

              <div class="stat-details">
                <p>Paid Commissions</p>
                <h3>{{ formatCurrency(snapshot.earnedCommissions) }}</h3>
              </div>
            </template>
          </div>
          <!-- Pending Commissions -->
          <div class="stat-card clickable" @click="openCommissions('P')">
            <template v-if="loading">

              <div class="shimmer-icon"></div>

              <div class="stat-details shimmer-details">
                <div class="shimmer shimmer-label"></div>
                <div class="shimmer shimmer-value"></div>
              </div>

            </template>

            <template v-else>

              <div class="stat-icon yellow">
                <i class="ph ph-hourglass-high"></i>
              </div>

              <div class="stat-details">
                <p>Pending Commissions</p>
                <h3>{{ formatCurrency(snapshot.commissionsPayable) }}</h3>
              </div>

            </template>
          </div>
          <!-- Pending Renewals -->
          <div class="stat-card">
            <template v-if="loading">

              <div class="shimmer-icon"></div>

              <div class="stat-details shimmer-details">
                <div class="shimmer shimmer-label"></div>
                <div class="shimmer shimmer-value"></div>
              </div>

            </template>

            <template v-else>

              <div class="stat-icon red">
                <i class="ph ph-warning-circle"></i>
              </div>

              <div class="stat-details">
                <p>Pending Renewals</p>
                <h3>{{ snapshot.pendingRenewals }} Policies</h3>
              </div>

            </template>

          </div>
          <!-- Active Quotes -->
          <div class="stat-card clickable" @click="goToQuotes">

            <template v-if="loading">

              <div class="shimmer-icon"></div>

              <div class="stat-details shimmer-details">
                <div class="shimmer shimmer-label"></div>
                <div class="shimmer shimmer-value"></div>
              </div>

            </template>

            <template v-else>

              <div class="stat-icon">
                <i class="ph ph-file-text"></i>
              </div>

              <div class="stat-details">
                <p>Active Quotes</p>
                <h3>
                  {{ snapshot.activeQuotes }}
                  <span class="tw-text-sm tw-font-normal c-gray-text">
                    / Ksh {{ (snapshot.activeQuotesValue / 1000).toFixed(0) }}k
                  </span>
                </h3>
              </div>

            </template>

          </div>

        </div>
      <!-- Pipeline Management Grid -->
      <div class="pipeline-grid">
        <!-- Closed Sales -->
        <div class="data-panel">
          <div class="panel-header">
            <h4 class="tw-flex tw-items-center tw-gap-1">
              <i class="ph ph-check-circle" style="color: #38A169; font-size: 20px;"></i>
              Closed Sales
            </h4>
            <router-link :to="{ name: 'AgentConversions' }" class="tw-text-sm c-gray-text" @click.native="trackViewAllConversions">View All</router-link>
          </div>
          <table class="data-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Product</th>
                <th>Value (Ksh)</th>
                <th class="tw-text-center">Activity</th>
              </tr>
            </thead>
            <tbody>
              <!-- Loading shimmer -->
              <template v-if="loading">
                <tr v-for="n in 4" :key="`conversion-shimmer-${n}`">
                  <td>
                    <div class="shimmer table-shimmer table-shimmer-client"></div>
                  </td>

                  <td>
                    <div class="shimmer table-shimmer table-shimmer-product"></div>
                  </td>

                  <td>
                    <div class="shimmer table-shimmer table-shimmer-value"></div>
                  </td>

                  <td class="tw-text-center">
                    <div class="shimmer table-shimmer table-shimmer-icon"></div>
                  </td>
                </tr>
              </template>

              <!-- Actual data -->
              <template v-else>
                <tr
                  v-for="(conv, idx) in visibleConversions"
                  :key="conv.policy || `conv-${idx}`"
                >
                  <td>{{ conv.client }}</td>

                  <td>
                    <span class="badge badge-info">
                      {{ conv.product }}
                    </span>
                  </td>

                  <td>{{ conv.value.toLocaleString() }}</td>

                  <td class="tw-text-center">
                    <div class="tw-flex tw-justify-center tw-gap-1">
                      <v-btn
                        icon
                        x-small
                        color="primary"
                        title="View Schedule"
                        @click="viewDocs(conv)"
                      >
                        <v-icon small>mdi-file-eye-outline</v-icon>
                      </v-btn>
                    </div>
                  </td>
                </tr>

                <!-- Empty state -->
                <tr v-if="recentConversions.length === 0">
                  <td colspan="4" class="empty-row">
                    No closed sales found
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <!-- Show More / Less toggle -->
          <div v-if="recentConversions.length > PANEL_LIMIT" class="show-more-row">
            <button class="show-more-btn" @click="toggleShowAllConversions">
              <i :class="showAllConversions ? 'ph ph-caret-up' : 'ph ph-caret-down'"></i>
              {{ showAllConversions ? 'Show Less' : `Show ${recentConversions.length - PANEL_LIMIT} More` }}
            </button>
          </div>
        </div>

        <!-- Active Quotes -->
        <div class="data-panel">
          <div class="panel-header">
            <h4 class="tw-flex tw-items-center tw-gap-1">
              <i class="ph ph-file-text" style="color: #2BABE2; font-size: 20px;"></i>
              Active Quotes
            </h4>
            <router-link :to="{ name: 'AgentQuotes' }" class="tw-text-sm c-gray-text" @click.native="trackViewAllQuotes">View All</router-link>
          </div>
          <table class="data-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Product</th>
                <th>Value (Ksh)</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
            <!-- Loading shimmer -->
            <template v-if="quotesLoading">
              <tr v-for="n in 4" :key="`quote-shimmer-${n}`">
                <td>
                  <div class="shimmer table-shimmer table-shimmer-client"></div>
                  <div class="shimmer table-shimmer table-shimmer-vehicle"></div>
                </td>

                <td>
                  <div class="shimmer table-shimmer table-shimmer-product"></div>
                </td>

                <td>
                  <div class="shimmer table-shimmer table-shimmer-value"></div>
                </td>

                <td>
                  <div class="shimmer table-shimmer table-shimmer-action"></div>
                </td>
              </tr>
            </template>

            <!-- Actual data -->
            <template v-else>
              <tr
                v-for="quote in visibleQuotes"
                :key="quote.quoteId || quote.client"
              >
                <td>
                  <div>{{ quote.client }}</div>

                  <div
                    v-if="quote.vehicle"
                    class="tw-text-xs c-gray-text tw-mt-0.5"
                  >
                    {{ quote.vehicle }}
                  </div>
                </td>

                <td>
                  <span class="badge badge-info">
                    {{ quote.product }}
                  </span>
                </td>

                <td>{{ quote.value.toLocaleString() }}</td>

                <td>
                  <v-btn
                    small
                    color="primary"
                    :to="resolveQuoteAction(quote)"
                    @click="trackConvertQuoteClick(quote)"
                  >
                    Convert
                  </v-btn>
                </td>
              </tr>

              <!-- Empty state -->
              <tr v-if="activeQuotes.length === 0">
                <td colspan="4" class="empty-row">
                  No active quotes
                </td>
              </tr>
            </template>
          </tbody>
          </table>
          <!-- Show More / Less toggle -->
          <div v-if="activeQuotes.length > PANEL_LIMIT" class="show-more-row tw-flex tw-items-center tw-justify-center tw-gap-3 tw-flex-wrap">
            <button v-if="moreQuotesCount > 0" class="show-more-btn" @click="toggleShowAllQuotes">
              <i :class="showAllQuotes ? 'ph ph-caret-up' : 'ph ph-caret-down'"></i>
              {{ showAllQuotes ? 'Show Less' : `Show ${moreQuotesCount} More` }}
            </button>
            <router-link
              v-if="activeQuotes.length > MAX_DASHBOARD_QUOTES"
              :to="{ name: 'AgentQuotes' }"
              class="show-more-link tw-text-xs tw-text-primary tw-font-semibold tw-no-underline hover:tw-underline tw-inline-flex tw-items-center tw-gap-1"
              @click.native="trackViewAllQuotes"
            >
              <span>View all {{ activeQuotes.length }} quotes in Activity</span>
              <i class="ph ph-arrow-right"></i>
            </router-link>
          </div>
        </div>

      </div>

      <!-- Product Showcase -->
      <h3 class="c-navy-text tw-text-lg tw-font-semibold tw-mb-4">Available Products</h3>
      <div class="product-tray">
        <div class="product-card" style="background: linear-gradient(135deg, #184679 0%, #112F56 100%);">
          <i class="ph ph-car"></i>
          <div>
            <h4>Motor Comprehensive</h4>
            <p>Full protection against loss, damage, and third-party liabilities.</p>
            <v-btn outlined :to="{ name: 'AgentProductSelect' }" class="tw-w-full" @click="trackGenerateQuoteClick">
              Generate Quote
            </v-btn>
          </div>
        </div>
        <div class="product-card" style="background: linear-gradient(135deg, rgba(43,171,226,0.5) 0%, rgba(0,140,191,0.5) 100%);">
          <i class="ph ph-airplane-tilt"></i>
          <div>
            <h4>Travel Insurance</h4>
            <p>Cover for trips abroad — Europe, worldwide, and more.</p>
            <v-btn outlined :to="{ name: 'AgentTravelPlanSelect' }" class="tw-w-full tw-mb-2">Generate Quote</v-btn>
            <v-btn color="primary" :to="{ name: 'AgentTravelPlanSelect', query: { journey: 'direct' } }" class="tw-w-full">Instant Buy</v-btn>
          </div>
        </div>
        <div class="product-card" style="background: linear-gradient(135deg, #184679 0%, #112F56 100%);">
          <i class="ph ph-first-aid"></i>
          <div>
            <h4>Personal Accident</h4>
            <p>Students on attachment — death, disability &amp; medical cover.</p>
            <v-btn outlined :to="{ name: 'AgentPaPlanSelect' }" class="tw-w-full" @click="trackGenerateQuoteClick">
              Generate Quote
            </v-btn>
          </div>
        </div>
        <div class="product-card" style="background: linear-gradient(135deg, rgba(24,70,121,0.5) 0%, rgba(13,58,133,0.5) 100%);">
          <i class="ph ph-briefcase"></i>
          <div>
            <h4>SME Package</h4>
            <p>Tailored protection for small and medium-sized businesses.</p>
            <v-btn outlined disabled class="tw-w-full">(Coming Soon)</v-btn>
          </div>
        </div>
      </div>

    </div>

    <!-- Agent Not Found Blocked State -->
    <div v-else-if="isAgentNotFound" class="agent-not-found-blocked">
      <div class="blocked-content">
        <div class="blocked-icon-circle">
          <i class="ph ph-user-circle-minus"></i>
        </div>
        <h2 class="blocked-title">Agent profile not found</h2>
        <p class="blocked-message">
          We couldn't locate an active agent profile associated with your account.
          Please contact support if you believe this is an error.
        </p>
        <div class="blocked-actions">
          <v-btn color="primary" block large @click="onSignOut">
            <i class="ph ph-sign-out tw-mr-2"></i>
            Sign Out
          </v-btn>
          <v-btn text color="primary" block class="tw-mt-2" @click="onRetryLoad">
            <i class="ph ph-arrows-clockwise tw-mr-2"></i>
            Try Again
          </v-btn>
        </div>
      </div>
    </div>
    <!-- Conversion Details Modal -->
    <v-dialog v-model="conversionModalVisible" max-width="480" persistent content-class="conversion-modal">
      <v-card v-if="selectedConversion" class="conversion-modal-card">
        <div class="conversion-modal-header">
          <h3 class="conversion-modal-title">Conversion Details</h3>
          <v-btn icon small @click="closeConversionModal" class="conversion-modal-close">
            <i class="ph ph-x"></i>
          </v-btn>
        </div>
        <v-divider></v-divider>
        <v-card-text class="conversion-modal-body">
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Client</span>
            <span class="conversion-detail-value">{{ selectedConversion.client }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Product</span>
            <span class="conversion-detail-value">
              <span class="badge badge-info">{{ selectedConversion.product }}</span>
            </span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Policy No</span>
            <span class="conversion-detail-value">{{ selectedConversion.policy || '—' }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Premium</span>
            <span class="conversion-detail-value">{{ formatCurrency(selectedConversion.value) }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Cover From</span>
            <span class="conversion-detail-value">{{ formatDate(selectedConversion.coverFrom) }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Cover To</span>
            <span class="conversion-detail-value">{{ formatDate(selectedConversion.coverTo) }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Renewal Date</span>
            <span class="conversion-detail-value">{{ formatDate(selectedConversion.renewalDate) }}</span>
          </div>
          <div class="conversion-detail-row" v-if="selectedConversion.batchNo">
            <span class="conversion-detail-label">Batch No</span>
            <span class="conversion-detail-value">{{ selectedConversion.batchNo }}</span>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="conversion-modal-actions">
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeConversionModal">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Documents Modal -->
    <v-dialog v-model="docsModalVisible" max-width="680" content-class="docs-modal">
      <v-card class="docs-modal-card">
        <div class="docs-modal-header">
          <div>
            <h3 class="docs-modal-title">Policy Documents</h3>
            <p class="docs-modal-subtitle">{{ docsModalTitle }}</p>
          </div>
          <v-btn icon small @click="docsModalVisible = false">
            <i class="ph ph-x" style="font-size:18px; color:#718096;"></i>
          </v-btn>
        </div>
        <v-divider></v-divider>

        <!-- Loading -->
        <div v-if="docsModalLoading" class="docs-modal-loader">
          <v-progress-circular indeterminate color="#2BABE2" size="36"></v-progress-circular>
          <p class="tw-mt-3 tw-text-sm" style="color:#94A3B8;">Fetching documents…</p>
        </div>

        <!-- Error -->
        <div v-else-if="docsModalError" class="docs-modal-empty">
          <i class="ph ph-warning-circle" style="font-size:40px; color:#FC8181;"></i>
          <p class="tw-mt-2 tw-text-sm" style="color:#718096;">{{ docsModalError }}</p>
        </div>

        <!-- Empty -->
        <div v-else-if="docsModalList.length === 0" class="docs-modal-empty">
          <i class="ph ph-folder-open" style="font-size:40px; color:#CBD5E0;"></i>
          <p class="tw-mt-2 tw-text-sm" style="color:#94A3B8;">No documents found for this policy.</p>
        </div>

        <!-- Document List -->
        <div v-else class="docs-modal-list">
          <div
            v-for="doc in docsModalList"
            :key="doc.id"
            class="doc-list-item"
          >
            <div class="doc-list-icon">
              <i
                :class="doc.mimeType === '.pdf' ? 'ph ph-file-pdf' : 'ph ph-file'"
                style="font-size:22px;"
                :style="{ color: doc.mimeType === '.pdf' ? '#E53E3E' : '#718096' }"
              ></i>
            </div>
            <div class="doc-list-info">
              <p class="doc-list-name">{{ doc.actualName }}</p>
              <div class="doc-list-meta">
                <span class="doc-type-badge">{{ doc.docType }}</span>
                <span class="doc-date">{{ doc.dateCreated }}</span>
              </div>
            </div>
            <v-btn
              small outlined color="primary"
              class="doc-view-btn"
              @click="openDocument(doc)"
            >
              <v-icon x-small left>mdi-eye-outline</v-icon>
              View
            </v-btn>
          </div>
        </div>

        <v-divider></v-divider>
        <v-card-actions style="padding: 12px 20px;">
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="docsModalVisible = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Document Viewer Modal -->
    <v-dialog
      v-model="docViewerVisible"
      max-width="1100"
      max-height="90vh"
      content-class="doc-viewer-dialog"
      @click:outside="closeDocViewer"
    >
      <v-card class="doc-viewer-card">
        <!-- Header -->
        <div class="doc-viewer-header">
          <div class="tw-flex tw-items-center tw-gap-3">
            <v-icon color="#E53E3E" size="22">mdi-file-pdf-box</v-icon>
            <div>
              <h3 class="doc-viewer-title">{{ docViewerTitle }}</h3>
              <p class="doc-viewer-subtitle">Document Preview</p>
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <v-btn
              v-if="docViewerUrl"
              small outlined color="primary"
              @click="openDocFull"
            >
              <v-icon x-small left>mdi-open-in-new</v-icon>
              Open Full
            </v-btn>
            <v-btn icon small @click="closeDocViewer">
              <v-icon size="20" color="#718096">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
        <v-divider></v-divider>

        <!-- Loading state -->
        <div v-if="docViewerLoading" class="doc-viewer-loading">
          <v-progress-circular indeterminate color="#00AEEF" size="48"></v-progress-circular>
          <p class="tw-mt-4" style="color:#94A3B8; font-size:0.9rem;">Loading document…</p>
        </div>

        <!-- iframe -->
        <div v-else-if="docViewerUrl" class="doc-viewer-frame-wrap">
          <iframe
            :src="docViewerUrl"
            class="doc-viewer-iframe"
            frameborder="0"
          ></iframe>
        </div>
      </v-card>
    </v-dialog>
  </agent-default>
</template>

<script>
import axios from 'axios';
import AgentDefault from '@/components/layout/AgentDefault';
import { mapGetters } from 'vuex';
import { trackAgentInteraction } from '@/utils/agentAnalytics';
import { buildShareableReferralLink } from '@/services/referralApi';

const CUSTOM_AGENT_CODE = 'custom:agent_code';
const PANEL_LIMIT = 4;
const MAX_DASHBOARD_QUOTES = 10;

export default {
  name: 'AgentDashboard',
  components: {
    AgentDefault,
  },
  data() {
    return {
      PANEL_LIMIT,
      MAX_DASHBOARD_QUOTES,
      copySuccess: false,
      showAllConversions: false,
      showAllQuotes: false,
      conversionModalVisible: false,
      selectedConversion: null,
      docsModalVisible: false,
      docsModalLoading: false,
      docsModalTitle: '',
      docsModalList: [],
      docsModalError: null,
      docViewerVisible: false,
      docViewerUrl: '',
      docViewerTitle: '',
      docViewerLoading: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser', 'isAuthenticated', 'isAgentNotFound']),
    ...mapGetters('portalCache', [
      'agentSummary',
      'agentConversions',
      'agentActiveQuotes',
      'agentSnapshot',
      'agentDashboardLoading',
    ]),
    loading() {
      return this.agentDashboardLoading;
    },
    quotesLoading() {
      return this.agentDashboardLoading && !this.agentActiveQuotes.length;
    },
    recentConversions() {
      return this.agentConversions;
    },
    activeQuotes() {
      return this.agentActiveQuotes;
    },
    snapshot() {
      return this.agentSnapshot;
    },
    referralLink() {
      const link = buildShareableReferralLink({
        referralLink: this.agentSummary?.agent?.referralLink,
        agentCode: this.authenticatedUser?.[CUSTOM_AGENT_CODE],
      });
      return link || 'Referral link not available — contact support to activate your agent code';
    },
    visibleConversions() {
      return this.showAllConversions
        ? this.recentConversions
        : this.recentConversions.slice(0, PANEL_LIMIT);
    },
    moreQuotesCount() {
      const maxShown = Math.min(this.activeQuotes.length, MAX_DASHBOARD_QUOTES);
      return Math.max(0, maxShown - PANEL_LIMIT);
    },
    visibleQuotes() {
      if (this.showAllQuotes) {
        return this.activeQuotes.slice(0, MAX_DASHBOARD_QUOTES);
      }
      return this.activeQuotes.slice(0, PANEL_LIMIT);
    },
  },
  mounted() {
    if (this.isAuthenticated) {
      this.$store.dispatch('portalCache/fetchAgentDashboard');
    }
  },
  methods: {
    trackNewQuoteClick() {
      trackAgentInteraction(this, 'dashboard_new_quote_click', {
        destination: 'AgentProductSelect',
      });
    },
    openCommissions(status) {
      const type = status === 'Y' ? 'paid' : 'pending';
      trackAgentInteraction(this, 'dashboard_commission_card_click', {
        commission_type: type,
        destination: 'AgentCommissions',
      });
      this.$router.push({ name: 'AgentCommissions', query: { pAuth: status } });
    },
    trackViewAllConversions() {
      trackAgentInteraction(this, 'dashboard_view_all_conversions', {
        destination: 'AgentConversions',
      });
    },
    toggleShowAllConversions() {
      this.showAllConversions = !this.showAllConversions;
      trackAgentInteraction(this, 'dashboard_toggle_conversions', {
        state: this.showAllConversions ? 'show_all' : 'show_less',
        total_rows: this.recentConversions.length,
      });
    },
    toggleShowAllQuotes() {
      this.showAllQuotes = !this.showAllQuotes;
      trackAgentInteraction(this, 'dashboard_toggle_quotes', {
        state: this.showAllQuotes ? 'show_all' : 'show_less',
        total_rows: Math.min(this.activeQuotes.length, MAX_DASHBOARD_QUOTES),
      });
    },
    goToQuotes() {
      trackAgentInteraction(this, 'dashboard_active_quotes_card_click', {
        destination: 'AgentQuotes',
      });
      this.$router.push({ name: 'AgentQuotes' });
    },
    trackViewAllQuotes() {
      trackAgentInteraction(this, 'dashboard_view_all_quotes', {
        destination: 'AgentQuotes',
      });
    },
    resolveQuoteAction(quote) {
      if (quote?.type === 'pa') {
        return {
          name: 'AgentPaSale',
          query: { quoteId: quote.quoteId },
        };
      }
      if (quote?.type === 'travel') {
        return {
          name: 'AgentTravelSale',
          query: { quoteId: quote.quoteId, journey: 'quote' },
        };
      }
      return {
        name: 'AgentSale',
        query: {
          quoteId: quote.quoteId,
          journey: 'direct',
        },
      };
    },
    trackConvertQuoteClick(quote) {
      let destination = 'AgentSale';
      if (quote?.type === 'pa') {
        destination = 'AgentPaSale';
      } else if (quote?.type === 'travel') {
        destination = 'AgentTravelSale';
      }
      trackAgentInteraction(this, 'dashboard_convert_quote_click', {
        quote_id: quote?.quoteId || 'unknown',
        product: quote?.product || 'unknown',
        destination,
      });
    },
    trackGenerateQuoteClick() {
      trackAgentInteraction(this, 'dashboard_generate_quote_click', {
        destination: 'AgentProductSelect',
      });
    },
    async onRetryLoad() {
      trackAgentInteraction(this, 'dashboard_retry_load');
      await this.$store.dispatch('portalCache/fetchAgentDashboard', { force: true });
    },
    async onSignOut() {
      trackAgentInteraction(this, 'dashboard_sign_out_click', {
        destination: 'SignIn',
      });
      await this.signOut();
    },
    formatCurrency(value) {
      if (value == null) return '';
      return `Ksh ${Number(value).toLocaleString()}`;
    },

    openConversionModal(conv) {
      this.selectedConversion = conv;
      this.conversionModalVisible = true;
    },

    closeConversionModal() {
      this.conversionModalVisible = false;
      this.selectedConversion = null;
    },
    async viewDocs(conv) {
      trackAgentInteraction(this, 'dashboard_view_documents_click', {
        policy_number: conv?.policy || conv?.batchNo || 'unknown',
      });
      const policyNo = conv.policy || conv.batchNo;
      if (!policyNo) return;

      this.docsModalTitle = `Documents — ${policyNo}`;
      this.docsModalList = [];
      this.docsModalError = null;
      this.docsModalVisible = true;
      this.docsModalLoading = true;

      try {
        const res = await axios.get(
          `/customer/document-api/v1/getNBDocByPolicyNo?policyNo=${policyNo}`,
          { validateStatus: () => true },
        );
        if (res.status === 200 && Array.isArray(res.data)) {
          const ALLOWED = ['.pdf', '.doc', '.docx'];
          this.docsModalList = res.data.filter(
            (doc) => ALLOWED.includes((doc.mimeType || '').toLowerCase()),
          );
        } else {
          this.docsModalError = 'Could not load documents for this policy.';
        }
      } catch {
        this.docsModalError = 'An error occurred while fetching documents.';
      } finally {
        this.docsModalLoading = false;
      }
    },

    openDocument(doc) {
      const docId = typeof doc === 'object' ? doc.id : doc;
      const docName = typeof doc === 'object' ? (doc.actualName || `Document #${docId}`) : `Document #${docId}`;
      trackAgentInteraction(this, 'dashboard_open_document', {
        document_id: docId,
        document_name: docName,
      });

      this.docViewerTitle = docName;
      this.docViewerLoading = false;
      const basePath = process.env.NODE_ENV === 'production' ? '/api' : '';
      this.docViewerUrl = `${basePath}/customer/document-api/v1/viewUWDocs/${docId}`;
      this.docViewerVisible = true;
    },

    closeDocViewer() {
      this.docViewerVisible = false;
      this.docViewerUrl = '';
    },

    openDocFull() {
      if (this.docViewerUrl) {
        trackAgentInteraction(this, 'dashboard_open_document_full', {
          document_name: this.docViewerTitle || 'unknown',
        });
        window.open(this.docViewerUrl, '_blank', 'noopener,noreferrer');
      }
    },

    formatDate(val) {
      if (!val) return '—';
      try {
        return new Date(val).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
      } catch { return '—'; }
    },
    async signOut() {
      await this.$store.dispatch('auth/signOut');
      this.$router.push({ name: 'SignIn' });
    },
    async copyReferralLink() {
      if (!this.referralLink || this.referralLink.startsWith('Referral link not available')) {
        this.$toast.error('Referral link is not available for your account yet');
        return;
      }
      try {
        await navigator.clipboard.writeText(this.referralLink);
        trackAgentInteraction(this, 'dashboard_copy_referral_link_success');
        this.copySuccess = true;
        this.$toast.success('Referral link copied to clipboard', 'Copied');
        setTimeout(() => { this.copySuccess = false; }, 2000);
      } catch {
        trackAgentInteraction(this, 'dashboard_copy_referral_link_failed');
        this.$toast.error('Failed to copy link', 'Error');
      }
    },
  },
  metaInfo: {
    title: 'AgentDashboard',
  },
};
</script>
<style scoped>
.dashboard-body {
  padding: 20px 24px;
}
@media (max-width: 960px) {
  .dashboard-body {
    padding: 16px 12px;
  }
}
.empty-row {
  text-align: center;
  padding: 20px 0;
  color: #a0aec0;
  font-size: 0.85rem;
}
.agent-not-found-blocked {
  height: calc(100vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.blocked-content {
  max-width: 400px;
  width: 100%;
  text-align: center;
  background: white;
  padding: 40px 30px;
  border-radius: 20px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.05);
  border: 1px solid #edf2f7;
}
.blocked-icon-circle {
  width: 80px;
  height: 80px;
  background: #fff5f5;
  color: #fc8181;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 40px;
}
.blocked-title {
  color: #1a202c;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 12px;
}

.blocked-message {
  color: #718096;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 30px;
}

.blocked-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.show-more-row {
  display: flex;
  justify-content: center;
  padding: 10px 0 4px;
  border-top: 1px solid #EDF2F7;
}

.show-more-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #2BABE2;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 6px;
  transition: background 0.15s;
}

.show-more-btn:hover {
  background: rgba(43, 171, 226, 0.08);
}

.clickable {
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
</style>

<style>
.conversion-modal .v-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.conversion-modal-card {
  border-radius: 12px;
}

.conversion-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 0;
}

.conversion-modal-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #184679;
  margin: 0;
}

.conversion-modal-close {
  color: #718096;
}

.conversion-modal-body {
  padding: 24px !important;
}

.conversion-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #E2E8F0;
  gap: 16px;
}

.conversion-detail-row:last-child {
  border-bottom: none;
}

.conversion-detail-label {
  font-size: 0.85rem;
  color: #718096;
  font-weight: 500;
  min-width: 120px;
}

.conversion-detail-value {
  font-size: 0.95rem;
  color: #2D3748;
  text-align: right;
}

.conversion-modal-actions {
  padding: 16px 24px !important;
}

.docs-modal-card { border-radius: 14px !important; }
.docs-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 20px 16px;
}
.docs-modal-title { font-size: 1.05rem; font-weight: 700; color: #184679; margin: 0; }
.docs-modal-subtitle { font-size: 0.78rem; color: #94A3B8; margin: 2px 0 0; }
.docs-modal-loader, .docs-modal-empty {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 40px 20px;
}
.docs-modal-list { max-height: 400px; overflow-y: auto; padding: 8px 0; }
.doc-list-item {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid #F1F5F9;
  transition: background 0.15s;
}
.doc-list-item:hover { background: #F8FAFC; }
.doc-list-item:last-child { border-bottom: none; }
.doc-list-icon { width: 32px; display: flex; justify-content: center; flex-shrink: 0; }
.doc-list-info { flex: 1; min-width: 0; }
.doc-list-name {
  font-size: 0.875rem; font-weight: 600; color: #2D3748;
  margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.doc-list-meta { display: flex; align-items: center; gap: 8px; margin-top: 3px; }
.doc-type-badge {
  font-size: 0.7rem; font-weight: 700;
  background: #EBF8FF; color: #2B6CB0;
  padding: 1px 7px; border-radius: 10px;
}
.doc-date { font-size: 0.75rem; color: #94A3B8; }
.doc-view-btn { flex-shrink: 0; }

.doc-viewer-card {
  border-radius: 14px !important;
  display: flex;
  flex-direction: column;
  /* Use viewport height so it's readable */
  height: 90vh;
  max-height: 90vh;
  overflow: hidden;
}
.doc-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #FAFBFC;
  flex-shrink: 0;
}
.doc-viewer-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0;
}
.doc-viewer-subtitle {
  font-size: 0.72rem;
  color: #94A3B8;
  margin: 0;
}
.doc-viewer-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 500px;
}
.doc-viewer-frame-wrap {
  flex: 1;
  overflow: hidden;
  display: flex;
}
.doc-viewer-iframe {
  width: 100%;
  /* Fill the full card height */
  height: 100%;
  min-height: 600px;
  border: none;
  display: block;
  flex: 1;
}
.doc-viewer-dialog {
  border-radius: 14px !important;
  overflow: hidden;
  /* Make it tall enough to read without opening full */
  margin: 24px auto !important;
}
</style>
